import {
  Box, Button, Card, CardContent, Chip, Grid, Stack, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import VideocamIcon from '@mui/icons-material/Videocam';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RuleIcon from '@mui/icons-material/Rule';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { PageHeader } from '@/components/PageHeader';
import { CenteredLoader } from '@/components/CenteredLoader';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useAuth } from '@/auth/AuthProvider';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime, formatRelative } from '@/utils/format';
import type { AppointmentListItem, MedicalConsent, Prescription } from '@/types/medical';

/**
 * Patient home.
 *
 * Read-only overview composed from three queries running in parallel:
 *   - upcoming appointments (status booked/confirmed/checked_in)
 *   - active prescriptions
 *   - consents that need attention (revoked or recently granted)
 *
 * The page is intentionally calm: hero greeting, three side-by-side cards,
 * each with a "see all" link to the dedicated section. No clinical alerts
 * here — those go on the dedicated pages where context exists.
 */

export function PatientHomePage() {
  const { user } = useAuth();

  const profileQ = useApiQuery({
    queryKey: ['patient', 'me'],
    queryFn:  patient.me.get,
  });
  const apptsQ = useApiQuery({
    queryKey: ['patient', 'appointments', 'all'],
    queryFn:  patient.appointments.list,
  });
  const rxQ = useApiQuery({
    queryKey: ['patient', 'prescriptions'],
    queryFn:  patient.prescriptions.list,
  });
  const consentsQ = useApiQuery({
    queryKey: ['patient', 'consents'],
    queryFn:  patient.consents.list,
  });

  const greeting = profileQ.data?.preferred_name
    || profileQ.data?.legal_first_name
    || user?.email?.split('@')[0]
    || 'there';

  const upcoming = (apptsQ.data ?? [])
    .filter((a) => ['booked', 'confirmed', 'checked_in'].includes(a.status))
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    .slice(0, 3);

  const activeRx = (rxQ.data ?? [])
    .filter((r) => ['signed', 'registered', 'partly_dispensed'].includes(r.status))
    .slice(0, 3);

  const recentConsents = (consentsQ.data ?? [])
    .filter((c) => !c.revoked_at)
    .sort((a, b) => new Date(b.granted_at).getTime() - new Date(a.granted_at).getTime())
    .slice(0, 3);

  const allLoading =
    profileQ.isPending && apptsQ.isPending && rxQ.isPending && consentsQ.isPending;
  if (allLoading) return <CenteredLoader label="Loading your overview…" />;

  return (
    <>
      <PageHeader
        title={`Hi, ${greeting}.`}
        subtitle="Your care at a glance."
      />

      <IntakeCard />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SectionCard
            title="Upcoming care"
            icon={<EventIcon color="primary" />}
            actionTo="/patient/appointments"
            actionLabel="See all"
            empty={upcoming.length === 0 && !apptsQ.isPending}
            emptyMsg="Nothing on your calendar."
            loading={apptsQ.isPending}
          >
            <Stack divider={<DividerThin />} spacing={1.5}>
              {upcoming.map((a) => <UpcomingApptRow key={a.id} appointment={a} />)}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard
            title="Active prescriptions"
            icon={<MedicalServicesIcon color="primary" />}
            actionTo="/patient/prescriptions"
            actionLabel="See all"
            empty={activeRx.length === 0 && !rxQ.isPending}
            emptyMsg="No active prescriptions."
            loading={rxQ.isPending}
          >
            <Stack divider={<DividerThin />} spacing={1.5}>
              {activeRx.map((r) => <PrescriptionRow key={r.id} rx={r} />)}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard
            title="Consents on file"
            icon={<RuleIcon color="primary" />}
            actionTo="/patient/consents"
            actionLabel="Manage"
            empty={recentConsents.length === 0 && !consentsQ.isPending}
            emptyMsg="No consents recorded yet."
            loading={consentsQ.isPending}
          >
            <Stack divider={<DividerThin />} spacing={1.5}>
              {recentConsents.map((c) => <ConsentRow key={c.id} consent={c} />)}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// MED-INTAKE-CARD
function IntakeCard() {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MoveToInboxIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Intake OS</Typography>
              <Typography variant="body2" color="text.secondary">
                Drop results, referrals or a whole folder - it gets read and organised for you.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            href="https://www.mybooking.ai/pl/intake/?profile=medical_patient"
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
          >
            Open Intake
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DividerThin() {
  return <Box sx={{ height: '1px', bgcolor: 'divider' }} />;
}

function SectionCard({
  title, icon, children, actionTo, actionLabel, empty, emptyMsg, loading,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  actionTo: string;
  actionLabel: string;
  empty: boolean;
  emptyMsg: string;
  loading: boolean;
}) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          {icon}
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        </Stack>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : empty ? (
          <Typography variant="body2" color="text.secondary">{emptyMsg}</Typography>
        ) : (
          children
        )}
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button
          component={RouterLink}
          to={actionTo}
          size="small"
          endIcon={<ChevronRightIcon />}
        >
          {actionLabel}
        </Button>
      </Box>
    </Card>
  );
}

function UpcomingApptRow({ appointment }: { appointment: AppointmentListItem }) {
  const isVideo = appointment.modality === 'video';
  return (
    <Box
      component={RouterLink}
      to={`/patient/appointments/${appointment.id}`}
      sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0, flexGrow: 1, mr: 2 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {appointment.procedure_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {appointment.clinician_name}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5}>
          <Typography variant="caption" className="tabular">
            {formatDateTime(appointment.start_at)}
          </Typography>
          {isVideo && (
            <Chip
              size="small" variant="outlined" icon={<VideocamIcon />}
              label="video" sx={{ height: 22 }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function PrescriptionRow({ rx }: { rx: Prescription }) {
  const itemCount = rx.items?.length ?? 0;
  const firstItem = rx.items?.[0];
  return (
    <Box
      component={RouterLink}
      to={`/patient/prescriptions/${rx.id}`}
      sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0, flexGrow: 1, mr: 2 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {firstItem?.drug_name_snapshot ?? 'Prescription'}
            {itemCount > 1 && (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}+ {itemCount - 1} more
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {rx.clinician_name} · {rx.jurisdiction_code}
          </Typography>
        </Box>
        <Typography variant="caption" className="tabular" color="text.secondary">
          {rx.valid_until ? `until ${formatDate(rx.valid_until)}` : '—'}
        </Typography>
      </Stack>
    </Box>
  );
}

function ConsentRow({ consent }: { consent: MedicalConsent }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box sx={{ minWidth: 0, flexGrow: 1, mr: 2 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ textTransform: 'capitalize' }}>
          {consent.consent_type.replace(/_/g, ' ')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          granted {formatRelative(consent.granted_at)}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={consent.granted ? 'granted' : 'declined'}
        color={consent.granted ? 'success' : 'default'}
        variant="outlined"
      />
    </Stack>
  );
}
